import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ComponentType,
  type ReactNode
} from 'react';
import type { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { TessellyFeatureFlagProvider } from './TessellyFeatureFlagProvider';
import type { TelemetrySink } from './telemetry';
import type { OpenFeatureApiLike } from './types';

export type FeatureFlagContextType = {
  provider: TessellyFeatureFlagProvider | null;
};

const featureFlagContext = createContext<FeatureFlagContextType>({ provider: null });

export type ProvideFeatureFlagsProps = {
  backend: ClientLibraryType | null;
  telemetry: TelemetrySink;
  openFeature: OpenFeatureApiLike;
  OpenFeatureProvider: ComponentType<{ children: ReactNode }>;
  children: ReactNode;
};

/**
 * How long the app tree stays blocked waiting for the flag provider before it
 * renders with default values. Generous enough to cover a slow-but-healthy
 * backend, short enough that a wedged one does not read as a broken app.
 */
const READY_TIMEOUT_MS = 8_000;

export const ProvideFeatureFlags = ({
  backend,
  telemetry,
  openFeature,
  OpenFeatureProvider,
  children
}: ProvideFeatureFlagsProps ) => {
  const [ isReady, setIsReady ] = useState( false );
  const [ provider, setProvider ] = useState<TessellyFeatureFlagProvider | null>( null );

  useEffect(() => {
    if ( !backend ) return;

    const tessellyProvider = new TessellyFeatureFlagProvider({ backend, telemetry });
    let cancelled = false;
    let readyTimer: ReturnType<typeof setTimeout> | undefined;

    /**
     * `isReady` gates the ENTIRE app tree below this provider (`return null`
     * while false), and the static HTML spinner is torn down the moment React
     * mounts — so an unsettled `setProviderAndWait` is not a slow start, it is a
     * permanently blank white page with nothing on screen and nothing in the
     * console.
     *
     * `setProviderAndWait` resolves or rejects on a normal outcome, and the
     * catch below already degrades to default flag values. What it does NOT do
     * is settle when the underlying request stalls — a saturated or wedged
     * backend leaves the promise pending forever, and the app never renders.
     *
     * So the wait is bounded. On timeout we render with default flag values,
     * exactly like the failure path; if the real provider settles afterwards it
     * is still installed (below), and the flags simply become live a moment
     * later. Degrading is always better than a white screen.
     */
    const markReady = () => {
      if ( cancelled ) return;
      clearTimeout( readyTimer );
      setIsReady( true );
    };

    readyTimer = setTimeout(() => {
      if ( cancelled ) return;
      console.warn(
        `[FeatureFlags] Provider did not become ready within ${ READY_TIMEOUT_MS }ms — ` +
        'rendering with default flag values. Flags will apply once it settles.'
      );
      setIsReady( true );
    }, READY_TIMEOUT_MS );

    openFeature.setProviderAndWait( tessellyProvider )
      .then(() => {
        // Runs even if the timeout already unblocked the tree: installing the
        // real provider late is what lets the flags go live without a reload.
        if ( cancelled ) return;
        setProvider( tessellyProvider );
        markReady();
      })
      .catch(( error ) => {
        console.error( '[FeatureFlags] Failed to initialize provider', error );
        // Still mark as ready so the app renders with default values
        markReady();
      });

    return () => {
      cancelled = true;
      clearTimeout( readyTimer );
      tessellyProvider.onClose();
      setProvider( null );
    };
    // telemetry / openFeature / OpenFeatureProvider are stable refs supplied by
    // the app shim; depending only on `backend` preserves prior behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backend]);

  if ( !isReady ) {
    return null;
  }

  return (
    <featureFlagContext.Provider value={{ provider }}>
      <OpenFeatureProvider>
        {children}
      </OpenFeatureProvider>
    </featureFlagContext.Provider>
  );
};

export const useFeatureFlagProvider = () => useContext( featureFlagContext );
